import type { Editor } from '@tiptap/core'
import TurndownService from 'turndown'
// @ts-expect-error - turndown-plugin-gfm doesn't have type definitions
import { gfm } from 'turndown-plugin-gfm'

// Block line mapping structure
export interface BlockLineMap {
  [blockId: string]: {
    startLine: number  // 0-based
    endLine: number    // 0-based
    content: string
  }
}

// Create line map from markdown source
// Tracks which source lines each block corresponds to
export const createLineMap = (markdown: string): BlockLineMap => {
  const lines = markdown.split('\n')
  const blocks: BlockLineMap = {}
  let currentBlockId = 0
  let currentBlockLines: number[] = []

  const isBlockBoundary = (line: string): boolean => {
    // Block boundaries: headings, lists, code blocks, blockquotes.
    // Thematic breaks are not blocks: injectBlockIds skips <hr>, so counting
    // `* * *` / `---` / `___` here shifts every later paragraph onto an earlier block.
    return /^(#{1,6}|[-*+]|\d+\.|>|```)/.test(line.trim())
  }

  const isThematicBreak = (line: string): boolean =>
    /^(?:(?:\*[ \t]*){3,}|(?:-[ \t]*){3,}|(?:_[ \t]*){3,})$/.test(line.trim())

  const finalizeBlock = () => {
    if (currentBlockLines.length > 0) {
      const id = `block-${currentBlockId++}`
      blocks[id] = {
        startLine: Math.min(...currentBlockLines),
        endLine: Math.max(...currentBlockLines),
        content: lines.slice(currentBlockLines[0], currentBlockLines[currentBlockLines.length - 1] + 1).join('\n')
      }
      currentBlockLines = []
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    // Empty line = block boundary
    if (trimmed === '') {
      finalizeBlock()
      continue
    }

    // Horizontal rules render as <hr> and get no data-block-id. Skip them so
    // block-N in the editor is the same paragraph as block-N in this map.
    if (isThematicBreak(trimmed)) {
      finalizeBlock()
      continue
    }

    // Special block markers (headings, lists, code blocks, blockquotes)
    const isMarker = isBlockBoundary(trimmed)

    if (isMarker && currentBlockLines.length > 0) {
      // Start new block
      finalizeBlock()
      currentBlockLines = [i]
    } else {
      // Continue current block
      currentBlockLines.push(i)
    }
  }

  // Finalize last block
  finalizeBlock()

  return blocks
}

// Inject block IDs into HTML
// Assigns sequential IDs to block elements (p, h1-h6, pre, blockquote, ul, ol)
// Note: lineMap parameter is kept for future use but not currently needed
export const injectBlockIds = (html: string, _lineMap: BlockLineMap): string => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  let blockIndex = 0
  const blockElements = doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, pre, blockquote, ul, ol')

  blockElements.forEach((el) => {
    const id = `block-${blockIndex++}`
    el.setAttribute('data-block-id', id)
  })

  return doc.body.innerHTML
}

// Create Turndown service for HTML → Markdown conversion
export const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  // Turndown appends "\n" itself. Use two spaces only so <br> becomes GFM "  \n".
  // Using "  \n" here produced "  \n\n", which markdown treats as paragraph breaks
  // (extra vertical space vs Google Docs soft returns).
  br: '  ',
  preformattedCode: false, // Don't wrap code in pre tags
})

// Enable GFM support (tables, strikethrough, task lists, etc.)
// Note: Must be added BEFORE custom rules to take precedence
turndownService.use(gfm)

// Remove unnecessary table attributes that might interfere with conversion
turndownService.remove(['colgroup', 'col'])

/**
 * Turndown's default escape() applies line-start rules (`1. `, `-`, `+ `, `# `) to each
 * text node in isolation. Inside headings that becomes `### 1\. Title` and similar noise.
 * Keep only character escapes that are always required in prose.
 */
const PROSE_ESCAPES: Array<[RegExp, string]> = [
  [/\\/g, '\\\\'],
  [/\*/g, '\\*'],
  [/`/g, '\\`'],
  [/\[/g, '\\['],
  [/\]/g, '\\]'],
  // Intentionally omit `_` — docs use snake_case IDs (PAR_FOO); GFM mid-word `_` is safe.
]

turndownService.escape = (string: string) =>
  PROSE_ESCAPES.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), string)

/**
 * Escape the opening of `[^n]:` footnote definitions before marked parses the document.
 * marked would read a short one (`[^3]: Ibid.`) as a link reference definition and drop it.
 * Escaped, it renders as literal `[^3]: Ibid.`; normalizeSerializedMarkdown restores it on save.
 */
export function protectFootnoteDefinitions(markdown: string): string {
  return markdown.replace(/^(\s{0,3})\[\^([^\]\s]+)\]:/gm, '$1\\[^$2\\]:')
}

/**
 * Post-Turndown cleanup: undo false-positive escapes and strip hard-break spaces that are
 * not mid-paragraph soft returns (list/table/heading lines, blank-line boundaries).
 */
export function normalizeSerializedMarkdown(markdown: string): string {
  const lines = (markdown ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
  let inFence = false

  const unescapeProse = (line: string): string => {
    let s = line
    // Footnote markers and definitions: turndown escapes the brackets (`\[^2\]`), which
    // turns a footnote into literal text. Also repairs docs already saved that way.
    s = s.replace(/\\\[\^([^\]\s\\]+)\\\]/g, '[^$1]')
    // ### 1\. Title / paragraph "1\. item"
    s = s.replace(/(\d)\\(\.)/g, '$1$2')
    // Mid-line punctuation TipTap/turndown historically over-escaped
    s = s.replace(/\\([+~#()=&])/g, '$1')
    // \- when not a line-start escaped bullet (keep "\- item" at column 0)
    s = s.replace(/([^\n])\\-/g, '$1-')
    // \_ in identifiers (PAR\_FOO); rare intentional \_ stays uncommon in our docs
    s = s.replace(/\\_/g, '_')
    return s
  }

  const isBlockStart = (line: string | undefined): boolean => {
    if (line === undefined || line.trim() === '') return true
    return /^(\s*[-*+] |\s*\d+\. |#{1,6}\s|\||-{3,}|\*{3,}|_{3,})/.test(line)
  }

  // A hard-break continuation line that starts like a list item (`- Horace` under an epigraph)
  // would reload as a bullet list. Turndown separates real lists from paragraphs with a blank
  // line, so a bullet directly after a "  "-terminated paragraph line can only come from a <br>.
  const isBreakedBulletLine = (i: number): boolean => {
    const prev = i > 0 ? lines[i - 1] : undefined
    return (
      prev !== undefined &&
      / {2}$/.test(prev) &&
      !isBlockStart(prev) &&
      /^(?:[-+*]|\d+[.)]) /.test(lines[i] ?? '')
    )
  }

  return lines
    .map((line, i) => {
      const trimmed = line.trimStart()
      if (trimmed.startsWith('```')) {
        inFence = !inFence
        return line
      }
      if (inFence) return line

      let s = unescapeProse(line)
      if (isBreakedBulletLine(i)) s = `\\${s}`
      if (/[ \t]+$/.test(s)) {
        const next = lines[i + 1]
        // Keep GFM "  \n" only for paragraph continuation (hard-breaks test).
        if (isBlockStart(next) && !isBreakedBulletLine(i + 1)) {
          s = s.replace(/[ \t]+$/, '')
        }
      }
      return s
    })
    .join('\n')
}

// All <br> (including TipTap Shift+Enter / hard-break class) use GFM two-space breaks.
// Do not emit markdown backslash breaks (\\\n): they loop with the loader and explode into
// lines containing only "\" when combined with marked breaks:true.

// Configure Turndown to handle paragraph spacing conservatively
turndownService.addRule('paragraphs', {
  filter: 'p',
  replacement: function (content, node) {
    const trimmed = content.trim()

    // Preserve empty paragraphs as blank lines (needed for spacing)
    // Check for data-blank-line attribute we added for <p><br></p> patterns
    if (trimmed === '' || node.getAttribute('data-blank-line') === 'true') {
      return '\n'
    }

    // Use standard markdown double-newline paragraph separator so that
    // marked.parse() correctly treats adjacent paragraphs as separate blocks
    // on the next load. Single \n caused silent paragraph-merge on round-trip.
    return '\n\n' + trimmed + '\n\n'
  }
})

const escapeHtmlAttr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

// Markdown image syntax cannot carry a size. Resized images round-trip as raw <img width>,
// which marked passes through on load and rehype-sanitize keeps in preview.
turndownService.addRule('sizedImages', {
  filter: (node) => node.nodeName === 'IMG' && /^\d+$/.test(node.getAttribute('width') || ''),
  replacement: (_content, node) => {
    const el = node as HTMLElement
    const src = escapeHtmlAttr(el.getAttribute('src') || '')
    const alt = escapeHtmlAttr(el.getAttribute('alt') || '')
    return `<img src="${src}" alt="${alt}" width="${el.getAttribute('width')}">`
  },
})

function isInsideTableCell(node: Node): boolean {
  let current: Node | null = node.parentNode
  while (current) {
    if (current.nodeName === 'TD' || current.nodeName === 'TH') return true
    current = current.parentNode
  }
  return false
}

// TipTap nests checkboxes inside <label>, not direct <li> children, so turndown-plugin-gfm
// taskListItems never matches. Emit GFM `- [ ]` / `- [x]` from TipTap taskItem nodes.
// Inside table cells: emit inline markers only - block lists break GFM pipe rows.
turndownService.addRule('tiptapTaskItems', {
  filter: function (node) {
    return node.nodeName === 'LI' && node.getAttribute('data-type') === 'taskItem'
  },
  replacement: function (content, node, options) {
    const checked = node.getAttribute('data-checked') === 'true'
    const checkbox = checked ? '[x]' : '[ ]'

    if (isInsideTableCell(node)) {
      const inline = content
        .replace(/^\n+/, '')
        .replace(/\n+$/, '')
        .trim()
        .replace(/\n+/g, ' ')
      return inline ? `${checkbox} ${inline}` : checkbox
    }

    content = content
      .replace(/^\n+/, '')
      .replace(/\n+$/, '\n')
      .trim()

    const prefix = options.bulletListMarker || '-'

    let depth = 0
    let currentNode = node.parentNode
    while (currentNode) {
      if (currentNode.nodeName === 'UL' || currentNode.nodeName === 'OL') {
        depth++
      }
      currentNode = currentNode.parentNode
    }
    depth = Math.max(0, depth - 1)
    const indent = '  '.repeat(depth)

    const lines = content.split('\n')
    const processedContent = lines.map((line, index) => {
      if (index === 0) return line
      if (line.match(/^[\s]*[-*+\d.]/)) {
        return '  ' + line
      }
      return line
    }).join('\n')

    return indent + prefix + ' ' + checkbox + ' ' + processedContent + '\n'
  },
})

// Configure Turndown to handle list items with proper nesting
// Exclude TipTap task items - handled by tiptapTaskItems rule above
turndownService.addRule('listItems', {
  filter: function (node) {
    if (node.nodeName !== 'LI') return false
    if (node.getAttribute('data-type') === 'taskItem') return false
    const parent = node.parentNode as Element | null
    if (parent?.nodeName === 'UL' && parent.getAttribute('data-type') === 'taskList') return false
    return true
  },
  replacement: function (content, node, options) {
    content = content
      .replace(/^\n+/, '') // Remove leading newlines
      .replace(/\n+$/, '\n') // Replace trailing newlines with a single one
      .trim()

    const prefix = options.bulletListMarker || '-'
    const parent = node.parentNode
    const isOrdered = parent && parent.nodeName === 'OL'

    // Calculate nesting depth by counting parent list elements
    let depth = 0
    let currentNode = node.parentNode
    while (currentNode) {
      if (currentNode.nodeName === 'UL' || currentNode.nodeName === 'OL') {
        depth++
      }
      currentNode = currentNode.parentNode
    }
    // Subtract 1 because we counted the immediate parent
    depth = Math.max(0, depth - 1)

    // Indent nested lists with 2 spaces per level
    const indent = '  '.repeat(depth)

    // Handle nested content (sub-lists) properly
    const lines = content.split('\n')
    const processedContent = lines.map((line, index) => {
      if (index === 0) return line // First line doesn't need extra indent
      // If line starts with list marker, it's a nested list - add indent
      if (line.match(/^[\s]*[-*+\d.]/)) {
        return '  ' + line
      }
      return line
    }).join('\n')

    if (isOrdered) {
      const start = (parent as Element).getAttribute('start')
      const index = Array.prototype.indexOf.call(parent.children, node)
      const number = start ? Number(start) + index : index + 1
      return indent + number + '. ' + processedContent + '\n'
    }

    return indent + prefix + ' ' + processedContent + '\n'
  }
})

function isMarkedTaskList(list: Element): boolean {
  const items = Array.from(list.children).filter((child) => child.tagName === 'LI')
  if (items.length === 0) return false
  return items.every(
    (li) => li.querySelector(':scope > input[type="checkbox"]') !== null
  )
}

/** TipTap TaskList schema is UL-only; marked emits OL for `1. [x]` GFM task items. */
function coerceMarkedTaskListToUl(list: Element, doc: Document): HTMLUListElement {
  if (list.tagName === 'UL') return list as HTMLUListElement

  const ul = doc.createElement('ul')
  while (list.firstChild) {
    ul.appendChild(list.firstChild)
  }
  list.parentNode?.replaceChild(ul, list)
  return ul
}

function normalizeTaskItemContent(li: Element, doc: Document): void {
  const children = Array.from(li.childNodes)
  const fragment = doc.createDocumentFragment()
  let inlineBuffer: Node[] = []

  const flushInline = () => {
    if (inlineBuffer.length === 0) return
    const paragraph = doc.createElement('p')
    inlineBuffer.forEach((node) => paragraph.appendChild(node))
    fragment.appendChild(paragraph)
    inlineBuffer = []
  }

  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent?.trim()) {
        inlineBuffer.push(child)
      }
      continue
    }

    if (child.nodeType !== Node.ELEMENT_NODE) continue

    const tag = (child as Element).tagName
    if (
      tag === 'UL' ||
      tag === 'OL' ||
      tag === 'DIV' ||
      tag === 'P' ||
      tag === 'PRE' ||
      tag === 'BLOCKQUOTE' ||
      /^H[1-6]$/.test(tag)
    ) {
      flushInline()
      fragment.appendChild(child)
    } else {
      inlineBuffer.push(child)
    }
  }

  flushInline()

  while (li.firstChild) {
    li.removeChild(li.firstChild)
  }
  li.appendChild(fragment)
}

/**
 * marked + gfm emits task checkboxes inside `<ul>` (`- [x]`) or `<ol>` (`1. [x]`).
 * TipTap only parses `ul[data-type="taskList"]` / `li[data-type="taskItem"]`.
 * Convert both shapes before setContent - OL must become UL or Visual mode drops
 * checkboxes and serializes a plain ordered list (preview -> edit data loss).
 */
export function convertMarkedTaskListsToTipTap(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html')
  const body = doc.body

  const lists = Array.from(body.querySelectorAll('ul, ol'))
  lists.sort((a, b) => {
    const depth = (el: Element) => {
      let d = 0
      let parent = el.parentElement
      while (parent) {
        if (parent.tagName === 'UL' || parent.tagName === 'OL') d++
        parent = parent.parentElement
      }
      return d
    }
    return depth(b) - depth(a)
  })

  for (const list of lists) {
    if (!isMarkedTaskList(list)) continue

    const ul = coerceMarkedTaskListToUl(list, doc)
    ul.setAttribute('data-type', 'taskList')
    ul.classList.add('task-list')

    for (const li of Array.from(ul.children)) {
      if (li.tagName !== 'LI') continue

      const checkbox = li.querySelector(':scope > input[type="checkbox"]') as HTMLInputElement | null
      if (!checkbox) continue

      const checked = checkbox.checked || checkbox.hasAttribute('checked')
      li.setAttribute('data-type', 'taskItem')
      li.classList.add('task-item')
      li.setAttribute('data-checked', checked ? 'true' : 'false')
      checkbox.remove()
      normalizeTaskItemContent(li, doc)
    }
  }

  return body.innerHTML
}

// Strip YAML frontmatter from markdown content
export function stripFrontmatter(content: string): string {
  const frontmatterRegex = /^---\s*\n[\s\S]*?\n---\s*\n/
  return content.replace(frontmatterRegex, '').trim()
}

/**
 * GFM pipe tables cannot hold block task lists. Collapse TipTap taskLists inside
 * td/th to inline `[x]` / `[ ]` markers before Turndown so rows stay one line.
 */
export function flattenTaskListsInTableCells(html: string): string {
  if (typeof DOMParser === 'undefined' || !/data-type=["']?taskList/.test(html)) {
    return html
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const cells = doc.querySelectorAll('td, th')
  for (const cell of cells) {
    const taskLists = cell.querySelectorAll('ul[data-type="taskList"]')
    for (const list of taskLists) {
      const parts: string[] = []
      for (const li of Array.from(list.querySelectorAll(':scope > li[data-type="taskItem"]'))) {
        const checked = li.getAttribute('data-checked') === 'true'
        const text = (li.textContent || '').replace(/\s+/g, ' ').trim()
        const marker = checked ? '[x]' : '[ ]'
        parts.push(text ? `${marker} ${text}` : marker)
      }
      list.replaceWith(doc.createTextNode(parts.join(' ')))
    }
  }
  return doc.body.innerHTML
}

/** Normalize TipTap HTML before Turndown (tables, blank lines, merged bold paragraphs). */
export function cleanEditorHtmlForTurndown(html: string): string {
  let cleaned = flattenTaskListsInTableCells(html)

  cleaned = cleaned.replace(/<table[^>]*>/g, '<table>')
  cleaned = cleaned.replace(/<col[^>]*>/g, '')
  cleaned = cleaned.replace(/<colgroup[^>]*>.*?<\/colgroup>/gs, '')
  cleaned = cleaned.replace(/<(td|th)([^>]*)><p>(.*?)<\/p><\/(td|th)>/gs, '<$1$2>$3</$4>')
  cleaned = cleaned.replace(/<p><br><\/p>/g, '<p data-blank-line="true"></p>')

  cleaned = cleaned.replace(
    /<p>((?:<strong>[^<]+?<\/strong>[^<]*?)\s+(?:<strong>[^<]+?<\/strong>[^<]*?)+)<\/p>/g,
    (match, content) => {
      const boldStatements = content.split(/\s+(<strong>)/)

      if (boldStatements.length > 2) {
        let result = '<p>' + boldStatements[0]

        for (let i = 1; i < boldStatements.length; i += 2) {
          result += '</p><p>' + boldStatements[i]
          if (boldStatements[i + 1]) {
            result += boldStatements[i + 1]
          }
        }

        result += '</p>'
        return result
      }
      return match
    }
  )

  return cleaned
}

/** Convert TipTap editor HTML to markdown using the shared Turndown pipeline. */
export function editorHtmlToMarkdown(html: string): string {
  return normalizeSerializedMarkdown(turndownService.turndown(cleanEditorHtmlForTurndown(html)))
}

export type ViewportFocusCoords = { clientX: number; clientY: number }

export type FocusTipTapOptions = {
  /** External scroll parent (preview/edit shared scroller). Restored after focus. */
  scrollParent?: HTMLElement | null
  /** scrollTop to keep; prevents ProseMirror focus from jumping the viewport. */
  preserveScrollTop?: number | null
}

/** Focus TipTap and place the caret at viewport coords, or at document end when coords are omitted. */
export function focusTipTapEditor(
  editor: Editor,
  coords?: ViewportFocusCoords | null,
  options?: FocusTipTapOptions,
): void {
  if (editor.isDestroyed) return

  const restoreScroll = () => {
    const parent = options?.scrollParent
    const top = options?.preserveScrollTop
    if (parent && top != null && Number.isFinite(top)) {
      parent.scrollTop = top
    }
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (editor.isDestroyed) return
      restoreScroll()

      try {
        if (coords) {
          const hit = editor.view.posAtCoords({ left: coords.clientX, top: coords.clientY })
          if (hit?.pos != null) {
            editor
              .chain()
              .focus(undefined, { scrollIntoView: false })
              .setTextSelection(hit.pos)
              .run()
            restoreScroll()
            return
          }
          // Coords miss (layout still settling): focus in place - do not jump to end.
          editor.commands.focus(undefined, { scrollIntoView: false })
          restoreScroll()
          return
        }

        const end = editor.state.doc.content.size
        editor
          .chain()
          .focus(undefined, { scrollIntoView: false })
          .setTextSelection(Math.max(1, end))
          .run()
        restoreScroll()
      } catch {
        try {
          editor.commands.focus(undefined, { scrollIntoView: false })
        } catch {
          // ignore focus errors on teardown
        }
        restoreScroll()
      }
    })
  })
}
